import { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatCurrency } from 'utils/formatCurrency';
import { cn } from 'utils/shadcn';

import { LTLQuote } from '../types';
import {
  LTLRateCardCoverageConfig,
  LTLRateCardCoverageDetails,
  LTLRateCardCoverageMode,
  shouldShowLTLRateCardCoverageSection,
} from './LTLRateCardCoverage';
import { RateMetadataView } from './LTLRateCardMetadata';
import { isTurvoRateMetadata } from './Turvo/RateCardMetadata';
import { TurvoRateMetadataView } from './Turvo/RateMetadataView';

interface LTLRateCardProps {
  quote: LTLQuote;
  isSelected: boolean;
  onClick: () => void;
  coverageConfig?: LTLRateCardCoverageConfig;
  hideCoverage?: boolean;
}

interface SimpleCostViewProps {
  totalCost: number;
  transitDays: number;
}

const formatPricingTypeLabel = (pricingType?: string): string => {
  if (!pricingType) {
    return '';
  }
  return pricingType;
};

function SimpleCostView({ totalCost, transitDays }: SimpleCostViewProps) {
  return (
    <Flex align='baseline' gap='sm'>
      <Typography variant='body-sm' weight='bold'>
        {formatCurrency(totalCost, 'USD')}
      </Typography>
      <Typography variant='body-xs' className='text-neutral-400'>
        {transitDays} day{transitDays !== 1 ? 's' : ''}
      </Typography>
    </Flex>
  );
}

function renderRateCostView(
  quote: LTLQuote,
  hasRateMetadata: boolean,
  hasTurvoRateMetadata: boolean
) {
  if (!hasRateMetadata) {
    return (
      <SimpleCostView
        totalCost={quote.totalCost}
        transitDays={quote.transitDays}
      />
    );
  }

  if (hasTurvoRateMetadata) {
    return (
      <TurvoRateMetadataView
        rateMetadata={quote.rateMetadata!}
        transitDays={quote.transitDays}
        externalId={quote.externalID}
      />
    );
  }

  return (
    <RateMetadataView
      rateMetadata={quote.rateMetadata!}
      transitDays={quote.transitDays}
    />
  );
}

export function LTLRateCard({
  quote,
  isSelected,
  onClick,
  coverageConfig,
  hideCoverage = false,
}: LTLRateCardProps) {
  const [showCoverage, setShowCoverage] = useState(
    coverageConfig?.defaultOpen ?? false
  );
  const pricingTypeLabel = formatPricingTypeLabel(
    quote.rateMetadata?.pricingType
  );

  const showCoverageSection =
    !hideCoverage &&
    shouldShowLTLRateCardCoverageSection(quote, coverageConfig);
  const coverageSectionTitle = coverageConfig?.sectionTitle ?? 'Insurance';
  const coverageMode =
    coverageConfig?.mode ?? LTLRateCardCoverageMode.Insurance;
  const useBrandCoverageSectionEmphasis =
    coverageMode === LTLRateCardCoverageMode.LimitedLiability;
  const hasTurvoRateMetadata = quote.rateMetadata
    ? isTurvoRateMetadata(quote.rateMetadata)
    : false;
  const hasRateMetadata = !!quote.rateMetadata;

  return (
    <TooltipProvider>
      <button
        type='button'
        onClick={onClick}
        className={cn(
          'w-full text-left p-4 rounded-lg border transition-all cursor-pointer mt-1 relative',
          'shadow-sm hover:shadow-md',
          isSelected
            ? 'border-brand-500 bg-brand-50 shadow-md'
            : 'border-neutral-200 bg-neutral-50 hover:border-brand-300 hover:bg-brand-50'
        )}
      >
        <div className='space-y-1'>
          <Flex direction='col' gap='xs'>
            <Flex align='center' gap='sm'>
              <Typography variant='body-sm' weight='semibold'>
                {quote.carrierName}
              </Typography>
              {!hasTurvoRateMetadata && pricingTypeLabel && (
                <span
                  className={cn(
                    'text-[11px] px-1.5 py-0.5 rounded border transition-colors',
                    isSelected
                      ? 'text-brand-700 bg-brand-100 border-brand-300'
                      : 'text-neutral-600 bg-neutral-100 border-neutral-300'
                  )}
                >
                  {pricingTypeLabel}
                </span>
              )}
              {quote.guaranteed && (
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded border transition-colors',
                    isSelected
                      ? 'text-brand-700 bg-brand-100 border-brand-300'
                      : 'text-neutral-500 bg-neutral-50 border-neutral-200'
                  )}
                >
                  Guaranteed
                </span>
              )}
              {quote.isVolume && (
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded border transition-colors',
                    isSelected
                      ? 'text-brand-700 bg-brand-100 border-brand-300'
                      : 'text-neutral-500 bg-neutral-50 border-neutral-200'
                  )}
                >
                  Volume
                </span>
              )}
            </Flex>
            {quote.tariffName && (
              <Typography variant='body-xs' className='text-neutral-500 mb-3'>
                {quote.tariffName}
              </Typography>
            )}
          </Flex>

          {renderRateCostView(quote, hasRateMetadata, hasTurvoRateMetadata)}
        </div>

        {showCoverageSection ? (
          <div
            className={cn(
              'border-t mt-3 pt-3 transition-colors',
              useBrandCoverageSectionEmphasis || isSelected
                ? 'border-brand-200'
                : 'border-neutral-100'
            )}
          >
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                setShowCoverage(!showCoverage);
              }}
              className={cn(
                'flex items-center justify-between w-full text-sm cursor-pointer transition-colors',
                useBrandCoverageSectionEmphasis || isSelected
                  ? 'text-brand-600 hover:text-brand-700'
                  : 'text-neutral-500 hover:text-neutral-600'
              )}
            >
              <span>{coverageSectionTitle}</span>
              {showCoverage ? (
                <ChevronUpIcon className='w-4 h-4' />
              ) : (
                <ChevronDownIcon className='w-4 h-4' />
              )}
            </button>
            {showCoverage && (
              <LTLRateCardCoverageDetails quote={quote} mode={coverageMode} />
            )}
          </div>
        ) : null}
      </button>
    </TooltipProvider>
  );
}
